import { useEffect, useState } from 'react';
import { B, radius } from '../constants/brand';
import { api } from '../api/client';
import { Btn, Spinner, Icon } from '../components/ui';
import { linkChipStyle } from './LinkPicker';
import { buildSuggestions } from '@harvus/shared';

// =============================================================
// SuggestLinksModal — "Suggest links & tasks" from the Harvi menu.
//
// Harvi reads the note (POST /api/dictation/parse, the Brain-dump router) and
// proposes the deals / capital items it is about and the tasks in it. Nothing
// is copied: a ticked record is LINKED (PUT /api/notebook/{id}/links), so the
// note shows on that record's feed and nowhere twice. A ticked task is created
// through the ordinary POST /api/tasks — its near-duplicate gate still applies,
// and a task the server thinks already exists stays here, flagged, rather than
// being created a second time.
//
// `text` is the note with its PDFs and photos already read into it, `files`
// what each file said, `unread` the files that could not be read
// (shared/src/noteRouting.ts prepareNoteForRouting). A link shares the note
// WITH its files, so nothing about the documents is copied anywhere.
// =============================================================

export default function SuggestLinksModal({ noteId, text, files = [], unread = [], existingLinks, isPrivate, onClose, onLinked }) {
  const [phase, setPhase] = useState('reading'); // reading | review | saving | error
  const [error, setError] = useState(null);
  const [links, setLinks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [allowTasks, setAllowTasks] = useState(false);
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.post('dictation/parse', { text }, { timeout: 90000 });
        if (cancelled) return;
        const s = buildSuggestions(res, existingLinks);
        setLinks(s.links);
        setTasks(s.tasks.map(t => ({ ...t, reqId: crypto.randomUUID() }))); // held across retries: a resend dedupes
        setAllowTasks(!!res.allowTasks);
        setPhase('review');
      } catch (e) {
        if (!cancelled) { setError(e.message || 'Harvi could not read the note.'); setPhase('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickedLinks = links.filter(l => l.checked);
  const pickedTasks = tasks.filter(t => t.checked && !t.created);

  const save = async () => {
    setPhase('saving');
    let linked = 0, created = 0, failed = 0;
    const msgs = [];
    if (pickedLinks.length) {
      try {
        const res = await api.put(`notebook/${noteId}/links`, { add: pickedLinks.map(l => l.ref) });
        linked = pickedLinks.length;
        onLinked?.(res);
        setLinks(ls => ls.filter(l => !l.checked));
      } catch (e) { msgs.push(e.message || 'The links could not be added.'); failed += pickedLinks.length; }
    }
    const next = [...tasks];
    for (const t of pickedTasks) {
      const i = next.findIndex(x => x.key === t.key);
      try {
        await api.post('tasks', {
          title: t.title, description: t.description || null,
          dealId: t.dealId, dealDetailId: t.dealDetailId, projectId: t.projectId,
          dueDate: t.dueDate, priority: t.priority || 'Medium', status: 'Not Started',
          assigneeIds: t.assigneeIds, clientRequestId: t.reqId,
        });
        next[i] = { ...next[i], created: true, checked: false, dup: null };
        created += 1;
      } catch (e) {
        if (e.status === 409 && e.body?.similarTasks?.length) {
          next[i] = { ...next[i], dup: e.body.similarTasks[0].title || 'an existing task' };
        } else {
          next[i] = { ...next[i], err: e.message || 'Could not create it.' };
          failed += 1;
        }
      }
    }
    setTasks(next);
    setOutcome({ linked, created, failed, dups: next.filter(t => t.dup && !t.created).length, msgs });
    setPhase('review');
  };

  const toggleLink = (key) => setLinks(ls => ls.map(l => (l.key === key ? { ...l, checked: !l.checked } : l)));
  const patchTask = (key, patch) => setTasks(ts => ts.map(t => (t.key === key ? { ...t, ...patch } : t)));

  return (
    <div role="dialog" aria-modal="true" aria-label="Suggest links and tasks"
      style={{ position: 'fixed', inset: 0, background: B.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
      onClick={phase === 'saving' ? undefined : onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: B.surface, borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: B.shadowXl }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 18px', borderBottom: `1px solid ${B.divider}` }}>
          <Icon name="sparkles" size={16} color={B.navy} />
          <strong style={{ fontSize: 15, color: B.text, flex: 1 }}>Suggest links &amp; tasks</strong>
          <button type="button" aria-label="Close" onClick={onClose} disabled={phase === 'saving'}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: B.textMd }}><Icon name="close" size={16} /></button>
        </div>

        <div style={{ padding: '12px 18px', overflowY: 'auto', flex: 1, fontSize: 13, color: B.text }}>
          {phase === 'reading' && <Spinner label="Harvi is reading the note…" />}
          {phase === 'error' && <div style={{ color: B.red }}>{error}</div>}
          {(phase === 'review' || phase === 'saving') && (
            <>
              {isPrivate && links.length > 0 && (
                <div style={{ padding: '8px 10px', borderRadius: 10, background: B.surfaceMuted, color: B.textMd, marginBottom: 10 }}>
                  This note is private. Linking it shares it with everyone who can see those records — they can read and edit it.
                </div>
              )}
              {files.some(f => f.read) && (
                <div style={{ color: B.textLt, marginBottom: 10 }}>
                  Harvi read {files.filter(f => f.read).map(f => f.name).join(', ')} with the note. Linking shares the note with its files.
                </div>
              )}
              {unread.length > 0 && (
                <div style={{ color: B.amber, marginBottom: 10 }}>
                  Not read: {unread.map(u => `${u.name} (${u.reason})`).join('; ')}.
                </div>
              )}

              <Section title="Link this note to">
                {links.length === 0 ? (
                  <Empty>{outcome?.linked ? 'Linked.' : 'Harvi found no deal or capital item to link it to.'}</Empty>
                ) : links.map(l => (
                  <label key={l.key} style={rowStyle}>
                    <input type="checkbox" checked={l.checked} onChange={() => toggleLink(l.key)} disabled={phase === 'saving'} />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={linkChipStyle(l.ref.type)}>{l.label}</span>
                      {l.confidence != null && (
                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: l.confidence >= 0.7 ? B.green : B.amber }}>
                          {Math.round(l.confidence * 100)}% match
                        </span>
                      )}
                      {l.excerpts.map((x, i) => (
                        <span key={i} style={excerptStyle}>“{x}”</span>
                      ))}
                    </span>
                  </label>
                ))}
              </Section>

              {allowTasks && (
                <Section title="Tasks in this note">
                  {tasks.length === 0 ? <Empty>No tasks found.</Empty> : tasks.map(t => (
                    <div key={t.key} style={{ ...rowStyle, opacity: t.created ? 0.6 : 1 }}>
                      <input type="checkbox" checked={t.checked} disabled={t.created || phase === 'saving'}
                        onChange={() => patchTask(t.key, { checked: !t.checked, err: null })} />
                      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <input value={t.title} disabled={t.created} onChange={e => patchTask(t.key, { title: e.target.value })}
                          style={{ font: 'inherit', fontSize: 13, padding: '5px 8px', border: `1px solid ${B.border}`, borderRadius: radius.md, color: B.text }} />
                        <span style={{ fontSize: 12, color: B.textMd }}>
                          on {t.label}{t.dueDate ? ` · due ${t.dueDate}` : ''}{t.priority ? ` · ${t.priority}` : ''}
                        </span>
                        {t.created && <span style={{ fontSize: 12, color: B.green, fontWeight: 600 }}>Created</span>}
                        {t.dup && !t.created && <span style={{ fontSize: 12, color: B.amber }}>Looks like an existing task: “{t.dup}”. Not created.</span>}
                        {t.err && <span style={{ fontSize: 12, color: B.red }}>{t.err}</span>}
                      </span>
                    </div>
                  ))}
                </Section>
              )}

              {outcome && (
                <div role="status" style={{ marginTop: 8, color: outcome.failed ? B.red : B.textMd }}>
                  {[
                    outcome.linked && `Linked to ${outcome.linked} record${outcome.linked === 1 ? '' : 's'}.`,
                    outcome.created && `Created ${outcome.created} task${outcome.created === 1 ? '' : 's'}.`,
                    outcome.dups && `${outcome.dups} left out as possible duplicates.`,
                    ...outcome.msgs,
                  ].filter(Boolean).join(' ') || 'Nothing changed.'}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 18px', borderTop: `1px solid ${B.divider}` }}>
          <Btn variant="secondary" size="sm" onClick={onClose} disabled={phase === 'saving'}>{outcome ? 'Done' : 'Cancel'}</Btn>
          <Btn size="sm" onClick={save} loading={phase === 'saving'}
            disabled={phase !== 'review' || (!pickedLinks.length && !pickedTasks.length)}>
            {buttonLabel(pickedLinks.length, pickedTasks.length)}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function buttonLabel(l, t) {
  const parts = [];
  if (l) parts.push(`link ${l} record${l === 1 ? '' : 's'}`);
  if (t) parts.push(`create ${t} task${t === 1 ? '' : 's'}`);
  if (!parts.length) return 'Nothing selected';
  const s = parts.join(' & ');
  return s[0].toUpperCase() + s.slice(1);
}

const rowStyle = { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: `1px solid ${B.divider}`, cursor: 'pointer' };
const excerptStyle = { display: 'block', marginTop: 4, fontSize: 12, color: B.textLt, fontStyle: 'italic' };

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}
const Empty = ({ children }) => <div style={{ color: B.textLt, padding: '6px 0' }}>{children}</div>;
